import {
  FREQUENCY_DISPLAY_NAMES,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';

import {
  IIndirectTaxCredentialField,
  IIndirectTaxPortal,
  IIndirectTaxRegistrationV2Response,
  IIndirectTaxRegistrationV2WithSettings,
  IndirectTaxJurisdictionType,
  TIndirectTaxRegisteredBy,
  TIndirectTaxRegistrationStatus,
  TIndirectTaxValidationStatus,
} from '@commenda/api/indirect-tax/registrations/types';
import { SettingsData } from '@commenda/api/new-sales-tax/corporations/types';
import { IIndirectTaxCredentialBlock } from '@commenda/api/new-sales-tax/credentials/types';
import {
  ICommendaNexus,
  NexusStatus,
} from '@commenda/api/new-sales-tax/nexus/types';
import { ICommendaOnboardingRegistration } from '@commenda/api/new-sales-tax/onboarding/types';
import { BadgeProps } from 'node_modules/@commenda/kakashi';
import {
  IndirectTaxRegistrationFiling,
  IndirectTaxRegistrationTab,
  IndirectTaxRegistrationTableFilter,
} from '../types';

export interface EnrichWithSettingsContext {
  corporationSettings?: ReadonlyArray<SettingsData>;
  // Map keyed by registration.jurisdictionId → resolved country/state.
  usStateJurisdictions?: ReadonlyMap<
    string,
    { country: string; state: string }
  >;
}

interface SettingBadgeConfig {
  label: string;
  variant: 'success' | 'gray';
}

interface FilingStatusBadgeConfig {
  label: string;
  variant: 'blue' | 'success' | 'error';
}

export interface IndirectTaxGroupedRegistrationsByStatus {
  completedRegistrations: ICommendaOnboardingRegistration[];
  inProgressRegistrations: ICommendaOnboardingRegistration[];
  statesWithNexusWithoutRegistration: ICommendaNexus[];
  registrationInReview: ICommendaOnboardingRegistration[];
}

export class IndirectTaxRegistrationsHelper {
  static readonly JURISDICTION_FILTER_KEY =
    IndirectTaxRegistrationTableFilter.JURISDICTION;

  static readonly DETAIL_TABS: ReadonlyArray<{
    id: IndirectTaxRegistrationTab;
    label: string;
  }> = [
    { id: IndirectTaxRegistrationTab.DETAILS, label: 'Details' },
    {
      id: IndirectTaxRegistrationTab.COMPANY_DETAILS,
      label: 'Company Details',
    },
    { id: IndirectTaxRegistrationTab.ACTIVITY, label: 'Activity' },
    { id: IndirectTaxRegistrationTab.FILINGS, label: 'Filings' },
    { id: IndirectTaxRegistrationTab.SETTINGS, label: 'Settings' },
    // TODO: Re-enable after local registration wizards are migrated to V2
    // {
    //   id: IndirectTaxRegistrationTab.LOCAL_REGISTRATIONS,
    //   label: 'Local registrations',
    // },
  ];

  static getSettingBadgeConfig(value: string): SettingBadgeConfig {
    if (value === 'Enabled') {
      return { label: 'Enabled', variant: 'success' };
    }
    return { label: 'Disabled', variant: 'gray' };
  }

  static getFilingStatusBadgeConfig(
    status: IndirectTaxRegistrationFiling['status']
  ): FilingStatusBadgeConfig {
    switch (status) {
      case 'Active':
        return { label: 'Active', variant: 'blue' };
      case 'Filed':
        return { label: 'Filed', variant: 'success' };
      case 'Overdue':
        return { label: 'Overdue', variant: 'error' };
    }
  }

  static getRegistrationTypeBadgeConfig(registeredBy: string): {
    label: string;
    variant: 'blue' | 'gray';
  } {
    if (registeredBy === 'COMMENDA') {
      return { label: 'New registration', variant: 'blue' };
    }
    return { label: 'Existing registration', variant: 'gray' };
  }

  static getRegistrationStatusBadgeConfig(
    registrationStatus: TIndirectTaxRegistrationStatus
  ): {
    label: string;
    variant: BadgeProps['variant'];
  } {
    switch (registrationStatus) {
      case TIndirectTaxRegistrationStatus.REGISTERED:
        return { label: 'Active', variant: 'success' };
      case TIndirectTaxRegistrationStatus.REGISTRATION_IN_PROGRESS:
        return { label: 'In progress', variant: 'warning' };
      case TIndirectTaxRegistrationStatus.PENDING:
        return { label: 'Pending', variant: 'gray' };
      default:
        return { label: registrationStatus, variant: 'gray' };
    }
  }

  static getValidationStatusBadgeConfig(
    validationStatus: TIndirectTaxValidationStatus
  ): { label: string; variant: BadgeProps['variant'] } {
    switch (validationStatus) {
      case TIndirectTaxValidationStatus.INVALID:
        return { label: 'Validation failed', variant: 'error' };
      case TIndirectTaxValidationStatus.VALID:
        return { label: 'Valid', variant: 'success' };
      case TIndirectTaxValidationStatus.PENDING:
        return { label: 'Validation pending', variant: 'warning' };
      case TIndirectTaxValidationStatus.VALIDATION_IN_PROGRESS:
        return { label: 'Validation in progress', variant: 'warning' };
      default:
        return { label: validationStatus, variant: 'gray' };
    }
  }

  static getValidationStatusColumnBadgeConfig(
    validationStatus: TIndirectTaxValidationStatus
  ): { label: string; variant: BadgeProps['variant'] } {
    const config: Record<
      TIndirectTaxValidationStatus,
      { label: string; variant: BadgeProps['variant'] }
    > = {
      [TIndirectTaxValidationStatus.INVALID]: {
        label: 'Invalid',
        variant: 'error',
      },
      [TIndirectTaxValidationStatus.VALID]: {
        label: 'Valid',
        variant: 'success',
      },
      [TIndirectTaxValidationStatus.PENDING]: {
        label: 'Pending',
        variant: 'warning',
      },
      [TIndirectTaxValidationStatus.VALIDATION_IN_PROGRESS]: {
        label: 'In progress',
        variant: 'warning',
      },
    };
    return config[validationStatus];
  }

  static getDerivedRegistrationStatusBadgeConfig(params: {
    registrationStatus: TIndirectTaxRegistrationStatus;
    validationStatus: TIndirectTaxValidationStatus;
    registeredBy: TIndirectTaxRegisteredBy;
    archivedAt?: string;
    effectiveEndDate?: string;
  }): { label: string; variant: BadgeProps['variant'] } {
    const {
      registrationStatus,
      validationStatus,
      registeredBy,
      archivedAt,
      effectiveEndDate,
    } = params;
    if (archivedAt) {
      return { label: 'Archived', variant: 'gray' };
    }
    if (
      effectiveEndDate &&
      NonTimezoneSensitiveDate.fromISO8601String(effectiveEndDate).lte(
        NonTimezoneSensitiveDate.now()
      )
    ) {
      return { label: 'Ended', variant: 'gray' };
    }
    if (
      registeredBy === TIndirectTaxRegisteredBy.COMMENDA ||
      validationStatus === TIndirectTaxValidationStatus.VALID
    )
      return this.getRegistrationStatusBadgeConfig(registrationStatus);
    return this.getValidationStatusBadgeConfig(validationStatus);
  }

  static formatFrequency(frequency: string | undefined): string {
    if (!frequency) return '—';
    return (
      // Safe: frequency values from API align with SalesTaxFilingFrequencyEnum
      FREQUENCY_DISPLAY_NAMES[frequency] ?? frequency
    );
  }

  static formatFrequencies(frequencies: string[] | undefined): string {
    if (!frequencies?.length) return '—';
    return frequencies.map((f) => FREQUENCY_DISPLAY_NAMES[f] ?? f).join(', ');
  }

  static formatEffectiveDate(dateString: string | undefined): string {
    if (!dateString) return '—';
    return NonTimezoneSensitiveDate.fromNativeDate(new Date(dateString)).format(
      'MMM DD, YYYY'
    );
  }

  static readonly TAX_TYPE_DISPLAY_NAMES: Record<string, string> = {
    RST: 'Retail Sales Tax',
    RUT: 'Retailer Use Tax',
    SUT: 'Sales & Use Tax',
    SSUT: 'Simplified Sellers Use Tax',
    RSSUT: 'Remote Sellers Sales & Use Tax',
    GRT: 'Gross Receipts Tax',
    GET: 'General Excise Tax',
    TPT: 'Transaction Privilege Tax',
    RTPT: 'Rental Transaction Privilege Tax',
    ROT: 'Retailers Occupation Tax',
    CUT: 'Compensating Use Tax',
    BAO: 'Business & Occupation Tax',
    BPT: 'Business Privilege Tax',
    BRT: 'Business Revenue Tax',
    VUT: 'Vendor Use Tax',
    RSST: 'Remote Seller Sales Tax',
    DTT: 'Destination Tax Type',
    RDF: 'Retail Delivery Fee',
    VAT: 'Value Added Tax',
    GST: 'Goods & Services Tax',
    HST: 'Harmonized Sales Tax',
    PST: 'Provincial Sales Tax',
    QST: 'Quebec Sales Tax',
    UOSS: 'Union One-Stop Shop',
    NUOSS: 'Non-Union One-Stop Shop',
    IOSS: 'Import One-Stop Shop',
  };

  static formatTaxTypes(taxTypes: string[] | undefined): string {
    if (!taxTypes?.length) return '—';
    return taxTypes.map((t) => this.TAX_TYPE_DISPLAY_NAMES[t] || t).join(', ');
  }

  static filterBySearch(
    registrations: ReadonlyArray<IIndirectTaxRegistrationV2WithSettings>,
    query: string
  ): IIndirectTaxRegistrationV2WithSettings[] {
    if (!query.trim()) return [...registrations];
    const lowerQuery = query.toLowerCase();
    return registrations.filter(
      (reg) =>
        (reg.registrationName ?? '').toLowerCase().includes(lowerQuery) ||
        (reg.taxRegistrationNumber ?? '').toLowerCase().includes(lowerQuery) ||
        (reg.jurisdictionName ?? '').toLowerCase().includes(lowerQuery)
    );
  }

  static enrichWithSettings(
    registration: IIndirectTaxRegistrationV2Response,
    ctx?: EnrichWithSettingsContext
  ): IIndirectTaxRegistrationV2WithSettings {
    return {
      ...registration,
      settingsApiFilingStatus: registration.managedFilingEnabled
        ? 'Enabled'
        : 'Disabled',
      settingsApiTaxCalculation:
        IndirectTaxRegistrationsHelper.resolveTaxCalculationStatus(
          registration,
          ctx
        ),
    };
  }

  private static resolveTaxCalculationStatus(
    registration: IIndirectTaxRegistrationV2Response,
    ctx?: EnrichWithSettingsContext
  ): 'Enabled' | 'Disabled' {
    if (registration.jurisdictionType !== 'STATE_OR_PROVINCE')
      return 'Disabled';
    const resolved = ctx?.usStateJurisdictions?.get(
      registration.jurisdictionId
    );
    if (!resolved || resolved.country !== 'US' || !resolved.state)
      return 'Disabled';
    const cjcEntry = ctx?.corporationSettings?.find(
      (entry) => entry.country === 'US' && entry.state === resolved.state
    );
    if (!cjcEntry) return 'Disabled';
    return cjcEntry.data?.disable_tax_calculation ? 'Disabled' : 'Enabled';
  }

  /**
   * Whether the registration holds every portal value the content marks as
   * required: credential-level fields live on the linked credential record,
   * registration-level fields are persisted as the tax registration number.
   * Without a resolved content portal, a linked credential is required.
   */
  static hasRequiredPortalDetails(
    registration: Pick<
      IIndirectTaxRegistrationV2Response,
      'credentialId' | 'taxRegistrationNumber'
    >,
    contentPortal: IIndirectTaxPortal | null
  ): boolean {
    if (!contentPortal) return !!registration.credentialId;

    const requiredFields = contentPortal.credentialFields.filter(
      (f) => f.required
    );
    const needsCredential = requiredFields.some(
      (f) => !f.isUniquePerRegistration
    );
    const needsRegistrationNumber = requiredFields.some(
      (f) => f.isUniquePerRegistration
    );

    if (needsCredential && !registration.credentialId) return false;
    if (needsRegistrationNumber && !registration.taxRegistrationNumber?.trim())
      return false;
    return true;
  }

  static readonly DEFAULT_REGISTRATION_NUMBER_LABEL = 'Tax ID';

  /**
   * Content labels the registration-level field (e.g. "VAT number"); its value
   * is the registration's taxRegistrationNumber.
   */
  static getRegistrationNumberLabel(
    contentPortal: IIndirectTaxPortal | null
  ): string {
    return (
      contentPortal?.credentialFields.find((f) => f.isUniquePerRegistration)
        ?.credentialName ??
      IndirectTaxRegistrationsHelper.DEFAULT_REGISTRATION_NUMBER_LABEL
    );
  }

  static getCredentialLevelFields(
    contentPortal: IIndirectTaxPortal
  ): IIndirectTaxCredentialField[] {
    return contentPortal.credentialFields
      .filter((f) => !f.isUniquePerRegistration && f.credentialType !== 'File')
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }

  /**
   * Older credentials can still hold registration-level blocks; those values
   * belong to the registration, so they are left out.
   */
  static getCredentialLevelBlocks(
    blocks: ReadonlyArray<IIndirectTaxCredentialBlock>,
    contentPortal: IIndirectTaxPortal | null
  ): IIndirectTaxCredentialBlock[] {
    const registrationLevelKeys = new Set(
      contentPortal?.credentialFields
        .filter((f) => f.isUniquePerRegistration)
        .map((f) => f.credentialKey)
    );
    return blocks
      .filter((block) => !registrationLevelKeys.has(block.blockKey))
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }

  static getJurisdictionFilterIds(
    filters: Record<string, string | string[]>
  ): string[] | undefined {
    const value =
      filters[IndirectTaxRegistrationsHelper.JURISDICTION_FILTER_KEY];
    if (!value) return undefined;
    const ids = Array.isArray(value) ? value : [value];
    return ids.length > 0 ? ids : undefined;
  }

  static applyFilters(
    registrations: ReadonlyArray<IIndirectTaxRegistrationV2WithSettings>,
    filters: Record<string, string | string[]>
  ): IIndirectTaxRegistrationV2WithSettings[] {
    let result = [...registrations];

    const taxTypeFilter = filters['TAX_TYPE'];
    if (
      taxTypeFilter &&
      Array.isArray(taxTypeFilter) &&
      taxTypeFilter.length > 0
    ) {
      result = result.filter((reg) =>
        reg.taxTypes?.some((t) => taxTypeFilter.includes(t))
      );
    }

    const frequencyFilter = filters['FILING_FREQUENCY'];
    if (
      frequencyFilter &&
      Array.isArray(frequencyFilter) &&
      frequencyFilter.length > 0
    ) {
      result = result.filter((reg) =>
        reg.frequencies?.some((f) => frequencyFilter.includes(f))
      );
    }

    const taxCalcFilter = filters['TAX_CALCULATION'];
    if (
      taxCalcFilter &&
      Array.isArray(taxCalcFilter) &&
      taxCalcFilter.length > 0
    ) {
      result = result.filter((reg) =>
        taxCalcFilter.includes(reg.settingsApiTaxCalculation)
      );
    }

    const managedFilingFilter = filters['MANAGED_FILING'];
    if (
      managedFilingFilter &&
      Array.isArray(managedFilingFilter) &&
      managedFilingFilter.length > 0
    ) {
      result = result.filter((reg) =>
        managedFilingFilter.includes(reg.settingsApiFilingStatus)
      );
    }

    return result;
  }

  /** The country's active state-level registrations. */
  static getStateRegistrations(
    registrations: ReadonlyArray<ICommendaOnboardingRegistration>,
    country: string
  ): ICommendaOnboardingRegistration[] {
    return registrations.filter(
      (reg) =>
        !reg.archivedAt &&
        reg.country === country &&
        reg.jurisdictionType === IndirectTaxJurisdictionType.STATE_OR_PROVINCE
    );
  }

  static getIndirectTaxGroupedRegistrationsByStatus(
    registrations: ReadonlyArray<ICommendaOnboardingRegistration>,
    nexusData: ReadonlyArray<ICommendaNexus>
  ): IndirectTaxGroupedRegistrationsByStatus {
    const completedRegistrations = registrations.filter(
      (reg) =>
        reg.registrationStatus === TIndirectTaxRegistrationStatus.REGISTERED
    );

    const inProgressRegistrations = registrations.filter(
      (reg) => reg.registrationStatus === TIndirectTaxRegistrationStatus.PENDING
    );

    const registrationInReview = registrations.filter(
      (reg) =>
        reg.registrationStatus ===
        TIndirectTaxRegistrationStatus.REGISTRATION_IN_PROGRESS
    );

    const statesWithNexusWithoutRegistration = nexusData.filter(
      (nexus) =>
        nexus.status === NexusStatus.NexusTriggered &&
        !registrations.some((reg) => reg.stateOrProvince === nexus.state)
    );

    return {
      completedRegistrations,
      inProgressRegistrations,
      statesWithNexusWithoutRegistration,
      registrationInReview,
    };
  }
}
